import { motion, AnimatePresence } from 'framer-motion';

const CHOICE_LABELS = ['A', 'B', 'C', 'D', 'E'];

const DIFFICULTY_CONFIG = {
  easy:   { label: 'Easy',   cls: 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30' },
  medium: { label: 'Medium', cls: 'bg-amber-500/20 text-amber-400 border border-amber-500/30' },
  hard:   { label: 'Hard',   cls: 'bg-red-500/20 text-red-400 border border-red-500/30' },
  Easy:   { label: 'Easy',   cls: 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30' },
  Medium: { label: 'Medium', cls: 'bg-amber-500/20 text-amber-400 border border-amber-500/30' },
  Hard:   { label: 'Hard',   cls: 'bg-red-500/20 text-red-400 border border-red-500/30' },
};

const BookmarkIcon = ({ filled }) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"
    fill={filled ? 'currentColor' : 'none'} stroke="currentColor"
    strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5">
    <path d="M19 21l-7-5-7 5V5a2 2 0 012-2h10a2 2 0 012 2z" />
  </svg>
);

function getChoiceState(index, question, selectedAnswer, showAnswer) {
  if (!showAnswer) {
    if (selectedAnswer === index) return 'selected';
    return 'default';
  }
  const correct = question.correctIndex ?? question.correct_index ?? question.answerIndex;
  if (index === correct) return 'correct';
  if (index === selectedAnswer) return 'wrong';
  return 'dimmed';
}

const choiceStateClasses = {
  default:  'bg-slate-700/40 border-slate-600/80 text-slate-100 hover:bg-slate-700/70 hover:border-teal-500/60 hover:text-white cursor-pointer',
  selected: 'bg-teal-500/15 border-teal-500/60 text-white cursor-pointer ring-1 ring-teal-500/30',
  correct:  'bg-emerald-500/15 border-emerald-500 text-emerald-200 cursor-default',
  wrong:    'bg-red-500/15 border-red-500 text-red-200 cursor-default',
  dimmed:   'bg-slate-800/50 border-slate-700/50 text-slate-500 cursor-default',
};

/**
 * QuestionCard
 * Props: question, onAnswer(choiceIndex), showAnswer, selectedAnswer, onFlag, isFlagged
 */
export default function QuestionCard({
  question,
  onAnswer,
  showAnswer = false,
  selectedAnswer = null,
  userAnswer = null,
  readOnly = false,
  onFlag,
  isFlagged = false,
}) {
  if (!question) return null;

  // Support both naming conventions
  const choices = question.choices ?? question.options ?? [];
  const questionText = question.text ?? question.question ?? '';
  const difficulty = question.difficulty ?? 'medium';
  const diffConf = DIFFICULTY_CONFIG[difficulty] ?? DIFFICULTY_CONFIG.medium;
  const resolvedSelected = selectedAnswer ?? userAnswer?.selectedIndex ?? null;
  const reveal = showAnswer && (readOnly || resolvedSelected !== null);

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.22, ease: 'easeOut' }}
      className="bg-slate-800/90 backdrop-blur-sm rounded-2xl border border-slate-700/80 shadow-xl shadow-black/20 overflow-hidden"
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-4 px-5 sm:px-6 pt-5 pb-3">
        <div className="flex flex-wrap items-center gap-2">
          <span className={`text-xs font-semibold px-2.5 py-0.5 rounded-full ${diffConf.cls}`}>
            {diffConf.label}
          </span>
          {question.category && (
            <span className="text-xs font-medium px-2.5 py-0.5 rounded-full bg-teal-500/10 text-teal-400 border border-teal-500/30">
              {question.category}
            </span>
          )}
          {(question.number || question.id) && (
            <span className="text-xs text-slate-500 font-mono">#{question.number ?? question.id}</span>
          )}
        </div>
        <motion.button
          whileTap={{ scale: 0.88 }}
          onClick={onFlag}
          className={`shrink-0 p-1.5 rounded-lg transition-colors duration-150 ${
            isFlagged
              ? 'text-amber-400 bg-amber-400/10 hover:bg-amber-400/20'
              : 'text-slate-500 hover:text-amber-400 hover:bg-amber-400/10'
          }`}
          aria-label={isFlagged ? 'Unflag question' : 'Flag question'}
        >
          <BookmarkIcon filled={isFlagged} />
        </motion.button>
      </div>

      {/* Question text */}
      <div className="px-5 sm:px-6 pb-5">
        <p className="text-slate-100 text-base sm:text-lg leading-relaxed font-medium">{questionText}</p>
        {question.imageUrl && (
          <img src={question.imageUrl} alt="Illustration"
            className="mt-4 rounded-xl border border-slate-700 max-h-56 object-contain" />
        )}
      </div>

      {/* Choices */}
      <div className="px-5 sm:px-6 pb-5 space-y-2.5">
        {choices.map((choice, i) => {
          const state = getChoiceState(i, question, resolvedSelected, reveal);
          return (
            <motion.button
              key={i}
              layout
              whileTap={state === 'default' || state === 'selected' ? { scale: 0.985 } : {}}
              disabled={readOnly || reveal}
              onClick={() => !(readOnly || reveal) && onAnswer?.(i)}
              className={`w-full flex items-start gap-3.5 px-4 py-3.5 rounded-xl border-2 transition-all duration-200 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-500 ${choiceStateClasses[state]}`}
            >
              <span className={`shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold border-2 transition-all duration-200 ${
                state === 'correct'  ? 'bg-emerald-500 border-emerald-400 text-white shadow-md shadow-emerald-500/30'
                : state === 'wrong'  ? 'bg-red-500 border-red-400 text-white shadow-md shadow-red-500/30'
                : state === 'selected' ? 'bg-teal-500/30 border-teal-400 text-teal-300'
                : state === 'dimmed' ? 'bg-slate-700/50 border-slate-600/50 text-slate-600'
                : 'bg-slate-700/60 border-slate-500/60 text-slate-300 group-hover:border-teal-500/60'
              }`}>
                {state === 'correct' ? (
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={3} strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                ) : state === 'wrong' ? (
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={3} strokeLinecap="round" strokeLinejoin="round" className="w-3.5 h-3.5">
                    <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                ) : (
                  CHOICE_LABELS[i]
                )}
              </span>
              <span className={`flex-1 text-sm leading-relaxed pt-1 ${state === 'correct' ? 'font-semibold' : ''}`}>{choice}</span>
            </motion.button>
          );
        })}
      </div>

      {/* Animated explanation reveal */}
      <AnimatePresence>
        {reveal && (
          <motion.div
            key="explanation"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35, ease: 'easeOut' }}
            className="overflow-hidden"
          >
            <div className="mx-5 sm:mx-6 mb-5 rounded-xl bg-slate-900/60 border border-slate-700/70 divide-y divide-slate-700/50">
              {question.explanation && (
                <div className="p-4 sm:p-5">
                  <div className="flex items-center gap-2 mb-2.5">
                    <span className="w-6 h-6 rounded-lg bg-teal-500/15 flex items-center justify-center">
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="w-3.5 h-3.5 text-teal-400">
                        <path d="M9 18h6M10 22h4M12 2a7 7 0 00-4 12.7V17h8v-2.3A7 7 0 0012 2z" />
                      </svg>
                    </span>
                    <p className="text-xs font-semibold text-teal-400 uppercase tracking-widest">
                      Explanation
                    </p>
                  </div>
                  <p className="text-slate-300 text-sm leading-relaxed">{question.explanation}</p>
                </div>
              )}
              {(question.citation || question.reference || question.pmid) && (
                <div className="p-4 sm:p-5">
                  <p className="text-xs font-semibold text-slate-400 uppercase tracking-widest mb-1">
                    Reference
                  </p>
                  {question.pmid ? (
                    <a
                      href={`https://pubmed.ncbi.nlm.nih.gov/${question.pmid}/`}
                      target="_blank" rel="noopener noreferrer"
                      className="text-xs text-teal-500 hover:text-teal-300 transition-colors"
                    >
                      PubMed PMID: {question.pmid} ↗
                    </a>
                  ) : (
                    <p className="text-slate-500 text-xs italic leading-relaxed">
                      {question.citation ?? question.reference}
                    </p>
                  )}
                </div>
              )}
              {question.teachingPoint && (
                <div className="p-4 sm:p-5 bg-teal-500/5">
                  <p className="text-xs font-semibold text-teal-400 uppercase tracking-widest mb-1">
                    Teaching Point
                  </p>
                  <p className="text-slate-300 text-sm leading-relaxed">{question.teachingPoint}</p>
                </div>
              )}
              {question.speaker && (
                <div className="p-3 sm:p-4 flex items-center gap-2">
                  <span className="text-xs text-slate-500 uppercase tracking-widest font-semibold">Speaker:</span>
                  <span className="text-xs text-slate-400 font-medium">{question.speaker}</span>
                  {question.source && (
                    <span className="ml-auto text-xs text-slate-600 italic">{question.source}</span>
                  )}
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
